import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, db } from "../firebase";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";

const AuthContext = createContext();

// ── Session helpers ────────────────────────────────────────────────────────
function getBrowserName(ua) {
  if (ua.includes("Chrome") && !ua.includes("Edg")) return "Chrome";
  if (ua.includes("Firefox"))                        return "Firefox";
  if (ua.includes("Safari") && !ua.includes("Chrome")) return "Safari";
  if (ua.includes("Edg"))                            return "Edge";
  if (ua.includes("Opera") || ua.includes("OPR"))   return "Opera";
  return "Unknown Browser";
}

function getOSName(ua) {
  if (ua.includes("Windows")) return "Windows";
  if (ua.includes("Mac OS"))  return "macOS";
  if (ua.includes("Linux"))   return "Linux";
  if (ua.includes("Android")) return "Android";
  if (ua.includes("iOS") || ua.includes("iPhone") || ua.includes("iPad")) return "iOS";
  return "Unknown OS";
}

async function getClientIP() {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);
    const res  = await fetch("https://api.ipify.org?format=json", { signal: controller.signal });
    clearTimeout(timeout);
    const data = await res.json();
    return data.ip || "Unknown";
  } catch {
    return "Unknown";
  }
}

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole]       = useState(null);
  const [companyId, setCompanyId]     = useState(null);
  const [loading, setLoading]         = useState(true);

  // ── Session recording (all roles) ────────────────────────────────────────
  // super_admin  → platform/super_admin_sessions/sessions/{id}
  // company user → companies/{companyId}/sessions/{id}
  async function recordSession(uid, role, cId) {
    try {
      const sessionId = `session_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      localStorage.setItem("pinpoint_session_id", sessionId);
      const ua = navigator.userAgent;
      const ip = await getClientIP();
      const data = {
        session_id:  sessionId,
        uid,
        role,
        ip_address:  ip,
        device: {
          userAgent:  ua,
          platform:   navigator.platform || "Unknown",
          language:   navigator.language || "en",
          screenSize: `${window.screen.width}x${window.screen.height}`,
          browser:    getBrowserName(ua),
          os:         getOSName(ua),
        },
        login_at:    serverTimestamp(),
        last_active: serverTimestamp(),
        active:      true,
        revoked:     false,
      };

      if (role === "super_admin") {
        await setDoc(doc(db, "platform", "super_admin_sessions", "sessions", sessionId), data);
      } else if (cId) {
        await setDoc(doc(db, "companies", cId, "sessions", sessionId), data);
      }
    } catch (err) {
      console.error("[SESSION] Failed to record session:", err.message);
      // Never block login if session recording fails
    }
  }

  // Returns the Firestore ref for the current session doc (null if not resolvable)
  function sessionDocRef(sessionId, role, cId) {
    if (!sessionId) return null;
    if (role === "super_admin") return doc(db, "platform", "super_admin_sessions", "sessions", sessionId);
    if (cId) return doc(db, "companies", cId, "sessions", sessionId);
    return null;
  }

  // ── Company status check ──────────────────────────────────────────────────
  // Called after sign-in to verify the company is active
  // Returns: { blocked: false } or { blocked: true, reason: "suspended" }
  async function checkCompanyStatus(cId) {
    if (!cId) return { blocked: false }; // super_admin has no companyId — always allow
    try {
      const snap = await getDoc(doc(db, "companies", cId));
      if (!snap.exists()) return { blocked: false };

      const company = snap.data();

      // 1. Manually disabled
      if (company.active === false) {
        return { blocked: true, reason: "suspended" };
      }

      // 2. Monthly plan — balance at or below 0
      if (
        company.billing_plan === "monthly" &&
        (company.billing_balance === 0 || company.billing_balance <= 0)
      ) {
        return { blocked: true, reason: "suspended" };
      }

      // 3. Expiry-date plan — date has passed
      if (company.billing_plan === "expiry_date" && company.subscription_expiry) {
        const expiry = company.subscription_expiry.toDate
          ? company.subscription_expiry.toDate()
          : new Date(company.subscription_expiry);
        if (expiry < new Date()) {
          return { blocked: true, reason: "suspended" };
        }
      }

      return { blocked: false };
    } catch (err) {
      // If we can't read the company doc, don't block — log and continue
      console.error("checkCompanyStatus error:", err.message);
      return { blocked: false };
    }
  }

  // ── Login ─────────────────────────────────────────────────────────────────
  async function login(email, password) {
    // Explicitly use localStorage persistence so this session stays in
    // localStorage and is NOT affected by the admin tab's sessionStorage
    await setPersistence(auth, browserLocalPersistence);
    const result = await signInWithEmailAndPassword(auth, email, password);

    // Get custom claims
    const token = await result.user.getIdTokenResult();
    const role  = token.claims.role;
    const cId   = token.claims.companyId;
    // docId is the Firestore document ID (may differ from Firebase Auth UID for vendors)
    const docId = token.claims.docId || result.user.uid;

    // ── Check company status BEFORE allowing access ──
    const status = await checkCompanyStatus(cId);
    if (status.blocked) {
      await signOut(auth); // sign out immediately
      const err = new Error("ACCOUNT_SUSPENDED");
      err.code  = "account_suspended";
      throw err;
    }

    // ── Check user account is active + restore language preference ──
    if (cId && role !== "super_admin") {
      try {
        const userSnap = await getDoc(doc(db, "companies", cId, "users", docId));
        if (userSnap.exists()) {
          if (userSnap.data().active === false) {
            await signOut(auth);
            const err = new Error("ACCOUNT_DISABLED");
            err.code  = "account_disabled";
            throw err;
          }
          // Apply user's saved language preference
          const savedLang = userSnap.data().language || localStorage.getItem("pinpoint_language") || "en";
          localStorage.setItem("pinpoint_language", savedLang);
          document.documentElement.dir  = savedLang === "ar" ? "rtl" : "ltr";
          document.documentElement.lang = savedLang;
        }
      } catch (err) {
        // Re-throw if it's our own disabled error
        if (err.code === "account_disabled") throw err;
        // Otherwise: can't read user doc — don't block, just continue
        console.error("checkUserActive error:", err.message);
      }
    }

    setUserRole(role);
    setCompanyId(cId);

    // Record login session for all roles (non-blocking)
    await recordSession(result.user.uid, role, cId);

    return { role, companyId: cId };
  }

  // ── Logout ────────────────────────────────────────────────────────────────
  async function logout() {
    try {
      // Mark session inactive before signing out
      const sessionId = localStorage.getItem("pinpoint_session_id");
      const ref = sessionDocRef(sessionId, userRole, companyId);
      if (ref) {
        try {
          await updateDoc(ref, { active: false, logout_at: serverTimestamp() });
        } catch (_) {}
      }
      if (sessionId) localStorage.removeItem("pinpoint_session_id");
      await signOut(auth);
    } finally {
      // Always reset RTL direction on logout so the login page shows correctly
      document.documentElement.dir  = "ltr";
      document.documentElement.lang = "en";
      localStorage.removeItem("pinpoint_language");
      setUserRole(null);
      setCompanyId(null);
    }
  }

  // ── Auth state listener ───────────────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const token = await user.getIdTokenResult();
        const role  = token.claims.role || null;
        const cId   = token.claims.companyId || null;

        // Re-check company status on every session restore
        const status = await checkCompanyStatus(cId);
        if (status.blocked) {
          await signOut(auth);
          setCurrentUser(null);
          setUserRole(null);
          setCompanyId(null);
          setLoading(false);
          return;
        }

        // Re-check user active flag on every session restore
        // Use docId claim (Firestore doc ID) — may differ from Firebase Auth UID for vendors
        const docId = token.claims.docId || user.uid;
        if (cId && role !== "super_admin") {
          try {
            const userSnap = await getDoc(doc(db, "companies", cId, "users", docId));
            if (userSnap.exists()) {
              if (userSnap.data().active === false) {
                await signOut(auth);
                setCurrentUser(null);
                setUserRole(null);
                setCompanyId(null);
                setLoading(false);
                return;
              }
              // Restore saved language preference on every session restore
              const savedLang = userSnap.data().language || localStorage.getItem("pinpoint_language") || "en";
              localStorage.setItem("pinpoint_language", savedLang);
              document.documentElement.dir  = savedLang === "ar" ? "rtl" : "ltr";
              document.documentElement.lang = savedLang;
            }
          } catch (err) {
            // Can't read user doc — don't block, just log
            console.error("onAuthStateChanged active check error:", err.message);
          }
        }

        setUserRole(role);
        setCompanyId(cId);
      } else {
        setUserRole(null);
        setCompanyId(null);
      }
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // ── Update last_active every 5 minutes (all roles) ────────────────────────
  useEffect(() => {
    const sessionId = localStorage.getItem("pinpoint_session_id");
    const ref = sessionDocRef(sessionId, userRole, companyId);
    if (!ref) return;
    const interval = setInterval(async () => {
      try { await updateDoc(ref, { last_active: serverTimestamp() }); } catch (_) {}
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [userRole, companyId]);

  // ── Check for session revocation every 2 minutes (all roles) ──────────────
  useEffect(() => {
    const sessionId = localStorage.getItem("pinpoint_session_id");
    const ref = sessionDocRef(sessionId, userRole, companyId);
    if (!ref) return;
    const interval = setInterval(async () => {
      try {
        const snap = await getDoc(ref);
        if (snap.exists() && snap.data().revoked === true) {
          console.log("[SESSION] Session revoked — signing out");
          localStorage.removeItem("pinpoint_session_id");
          await signOut(auth);
        }
      } catch (_) {}
    }, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [userRole, companyId]);

  const value = {
    currentUser,
    userRole,
    companyId,
    login,
    logout,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
